import { useContext, useEffect, useState } from 'react';

// mui
import { Box, Checkbox, FormControlLabel, makeStyles, Popover, Typography } from '@material-ui/core';
import ClearIcon from '@material-ui/icons/Clear';
import DoneIcon from '@material-ui/icons/Done';
import { KeyboardDatePicker } from '@material-ui/pickers';

// app
import { Button, FormGrid, Tooltip } from 'components';

import { RiskDetailsContext } from 'pages/RiskDetails/RiskDetails';
import * as utils from 'utils';
import { InfoOutlined } from '@material-ui/icons';

const useStyles = makeStyles((theme) => ({
  bindBox: {
    padding: theme.spacing(2),
    maxWidth: '300px',
  },
  dateLabel: {
    '& label': {
      color: theme.palette.neutral.darker,
      fontSize: `${theme.typography.pxToRem(16)}!important`,
      fontWeight: '600!important',
    },
  },
  btnMarginLeft: {
    marginLeft: 10,
  },
  disclosurePopover: {
    maxWidth: 500,
  }
}));

export const BindPopover = ({
  idBind,
  openBind,
  anchorElBind,
  handleCloseBindPopover,
  handleBindQuoteAndClosePopover,
  earliestBindPeriod,
  enablePreBindConfirmation,
  preBindConfirmationText,
}) => {
  const { risk } = useContext(RiskDetailsContext);

  const daysDiff = risk?.defaultPolicyDuration ?? utils.date.diffDays(risk?.expiryDate, risk?.inceptionDate);
  const classes = useStyles();
  const [confirmationChecked, setConfirmationChecked] = useState(false);
  const [selectedInceptionDate, setInceptionDate] = useState(risk?.inceptionDate);
  const [selectedExpiryDate, setExpiryDate] = useState(utils.date.addDays(risk?.inceptionDate, daysDiff));
  const [isValidDate, setIsValidDate] = useState(true);
  const today = utils.date.today();

  const [accepted, setAccepted] = useState(false);

  const handleInceptionDateChange = (date) => {
    setInceptionDate(date);
    setExpiryDate(utils.date.addDays(utils.date.startOf(date), daysDiff));
  };

  useEffect(() => {
    const isBindable =
      utils.date.diffDays(selectedInceptionDate, today) <= earliestBindPeriod &&
      utils.date.isSameOrAfter(selectedExpiryDate, utils.date.addDays(today, earliestBindPeriod));
    if (utils.date.isValid(selectedInceptionDate) && utils.date.isValid(selectedExpiryDate) && isBindable) {
      setIsValidDate(true);
    } else {
      setIsValidDate(false);
    }
  }, [selectedInceptionDate, selectedExpiryDate, earliestBindPeriod, today]);

  const disabled = !accepted || !isValidDate || (enablePreBindConfirmation && !confirmationChecked);

  return (
    <Popover
      id={idBind}
      open={openBind}
      anchorEl={anchorElBind}
      onClose={handleCloseBindPopover}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
    >
      <Box className={classes.bindBox} data-testid="bind-popover">
        <FormGrid container spacing={1} justifyContent="center" alignItems="top">
          <FormGrid
            item
            xs={6}
            classes={{
              root: classes.dateLabel,
            }}
          >
            <KeyboardDatePicker
              autoOk
              value={selectedInceptionDate}
              placeholder="21/10/2022"
              onChange={(date) => handleInceptionDateChange(date)}
              maxDate={utils.date.addDays(today, earliestBindPeriod)}
              format="DD-MM-yyyy"
              label="Inception Date"
              maxDateMessage={utils.string.t('products.notBindable', { days: earliestBindPeriod })}
            />
          </FormGrid>
          <FormGrid
            item
            xs={6}
            classes={{
              root: classes.dateLabel,
            }}
          >
            <KeyboardDatePicker
              autoOk
              value={selectedExpiryDate}
              placeholder="21/10/2023"
              format="DD-MM-yyyy"
              disablePast
              label="Expiry Date"
              disabled
            />
          </FormGrid>
          {enablePreBindConfirmation ? (
            <FormGrid item xs={12}>
              <Box display="flex" width="100%" flexDirection="row" justifyContent="flex-start" alignContent="center">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={confirmationChecked}
                      onChange={(e) => setConfirmationChecked(e.target.checked)}
                      name="checkedB"
                      color="primary"
                    />
                  }
                  label={preBindConfirmationText}
                />
              </Box>
            </FormGrid>
          ) : null}
          <FormGrid item xs={12}>
            <Box display="flex" width="100%" flexDirection="row" justifyContent="flex-start" fontSize={11} alignItems="center">
              <Checkbox checked={accepted} onChange={(e) => setAccepted(e.target.checked)} name="accepted" color="primary" size='small' />
                <Typography variant="body2" style={{ fontWeight: 400, display: 'inline', marginRight: 5, fontSize: 11 }}>
                  I have read and understood the risk disclosure
                </Typography>
                <Tooltip
                  rich
                  placement="top"
                  interactive
                  nestedClasses={{
                    tooltip: classes.disclosurePopover
                  }}
                  title={
                    <Box>
                      <Typography variant="body2" style={{ fontWeight: 400, marginBottom: 12, fontSize: 11, textAlign: 'left' }}>
                        By clicking “Bind”, you are accepting the quote on behalf of the (re)insured and such acceptance will create legally enforceable obligations by binding the (re)insured to the policy. Before clicking “Bind”, you should ensure that you have reviewed and understand the terms of the quote and once bound, review the policy documentation. The insurance would be underwritten on a “Claims Made” basis and you should refer to the policy documentation for the exact “Claims Made” coverage and requirements. If the (re)insured becomes aware of a claim or circumstances giving rise to a claim it is essential that they should notify us promptly.
                      </Typography>
                      <Typography variant="body2" style={{ fontWeight: 400,  fontSize: 11, textAlign: 'left' }}>
                        Failure to pay the premium for the policy may result in it being cancelled. All material information must be disclosed both at the time of accepting a quote and during the period of the contract where either requested to do so by the underwriters or where there is a significant change in the risk. If there is any doubt whether information is material, it should be disclosed to underwriters. Any failure to disclose material information to underwriters may entitle them to avoid the contract from inception.
                      </Typography>

                    </Box>
                  }
                >
                  <InfoOutlined fontSize="small" />
                </Tooltip>
              </Box>
          </FormGrid>
          <FormGrid item xs={12}>
            <Box display="flex" width="100%" flexDirection="row" justifyContent="flex-end">
              <Button
                data-testid="bind-ok-button"
                size="xsmall"
                icon={DoneIcon}
                color="default"
                style={{ backgroundColor: !disabled ? 'rgb(44, 198, 171)' : '', color: !disabled ? 'white' : '' }}
                nestedClasses={{ icon: classes.icon }}
                onClick={() =>
                  handleBindQuoteAndClosePopover(utils.date.toISOString(selectedInceptionDate), utils.date.toISOString(selectedExpiryDate))
                }
                disabled={disabled}
              />
              <Button
                data-testid="bind-cancel-button"
                size="xsmall"
                icon={ClearIcon}
                color="default"
                nestedClasses={{ btn: classes.btnMarginLeft, icon: classes.icon }}
                onClick={handleCloseBindPopover}
              />
            </Box>
          </FormGrid>
        </FormGrid>
      </Box>
    </Popover>
  );
};
